import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { DetailsList, SelectionMode, IconButton, Spinner } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useConfirmDialog } from '@bingads-webui-campaign-react/common-hooks';

import { getColumns } from '../utils/booking-config';
import { SmartBookingModal } from './booking-modal-base';
import { AppointmentEdit } from './edit-appoinment';

const getStyles = () => ({
  container: {
    width: '100%',
    position: 'relative',
  },
  listContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',

    '& .ms-DetailsList-headerWrapper .ms-DetailsHeader': {
      borderBottom: 'none',
    },

    '& .ms-Spinner, .empty-message': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },

    '& .list-header': {
      height: '84px',
      lineHeight: '84px',
      fontSize: 20,
      fontWeight: 600,
      margin: 0,
    },

    '& .list-content': {
      minHeight: 60,
      flex: 1,
    },
  },
  columnCell: {
    borderRight: '1px solid #F3F2F1',
    whiteSpace: 'normal',
    fontSize: 14,

    '&:last-child': {
      borderRight: 'none',
    },
  },
  topBottomBorder: {
    borderTop: '1px solid #979797',
    borderBottom: '1px solid #979797',
  },
  editingHeader: {
    width: '100%',
    paddingLeft: 32,
    lineHeight: '65px',
    fontWeight: 600,

    '& .back-to-list': {
      fontSize: 20,
      height: 65,

      '& button': {
        color: 'black',
        marginRight: 10,
      },
    },

    '& .title': {
      fontSize: 24,
      height: 65,
      textAlign: 'center',
    },
  },
});

const editingModalStyle = {
  main: {
    width: '590px',
    height: '950px',
  },
};

const backIcon = { iconName: 'Back' };

export const UnStyledSmartAppointmentManagement = ({
  i18n,
  logger,
  classes,
  className,
  title,
  customerEmailAddress,
  customerTimeZoneOffset,
  appointmentHours,
  userLocale,
  smartPageId,
  businessName,
  smartPageService,
  onInternalModalChange,
  modalStyle,
  // whethe to show "go back apponintments"
  showBack,
}) => {
  const submitFnRef = useRef(null);

  const [isEditingModalVisible, setIsEditingModalVisible] = useState(false);
  const [shouldFetchBookings, setShouldFetchBookings] = useState(true);
  const [isFetchBookings, setIsFetchBookings] = useState(false);
  const [isUpdateBooking, setIsUpdateBooking] = useState(false);

  const [existingBookings, setExistingBookings] = useState([]);
  const [editingItem, setEditingItem] = useState({});

  // Update specific appointment
  const upsertSmartPageBookings = useCallback((bookingDetail) => {
    const {
      protocol,
      host,
      pathname,
    } = window.location;

    const parameters = {
      manageAppointmentLink: `${protocol}//${host}${pathname}`,
      customerEmailAddress,
      smartPageId,
      bookingDetail,
      businessName,
      customerTimeZoneOffset,
      userLocale,
    };

    setIsUpdateBooking(true);
    logger.logTrace('CTA|BookAppointment|UpdateAppointment');

    return smartPageService.upsertSmartPageBookings(parameters)
      .then((res) => {
        setShouldFetchBookings(true);

        return res;
      }).catch((err) => {
        const errorMessage = (err && err.body && err.body.value)
          || i18n.getString(_TL_('Request failed, please try later'));

        logger.logError('CTAError|BookAppointment|UpdateAppointment', err);
        throw errorMessage;
      }).finally(() => {
        setIsUpdateBooking(false);
      });
  }, [
    smartPageService, smartPageId, logger, customerEmailAddress,
    businessName, i18n, customerTimeZoneOffset, userLocale,
  ]);

  // Handler for clicking cancel appointment button in confirm dialog
  const onConfirmCancelAppointment = useCallback(() => {
    logger.logTrace('CTA|BookAppointment|CancelAppointment');

    upsertSmartPageBookings({
      ...editingItem,
      status: 'Deleted',
    }).then(() => {
      // Refetch the data to update the list
      setShouldFetchBookings(true);
    }).catch((error) => {
      logger.logError('CTAError|BookAppointment|CancelAppointment', error);

      throw new Error(error);
    });
  }, [editingItem, logger, upsertSmartPageBookings]);

  const [
    cancelAppointmentDialog,
    showCancelAppointmentDialog,
    isCancelAppointmentDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('Are you sure you want to cancel this appointment?')),
    content: i18n.getString(_TL_('If you cancel, we\'ll email the customer who booked the appointment informing them of the cancellation.')),
    confirmText: i18n.getString(_TL_('Cancel appointment')),
    onConfirm: onConfirmCancelAppointment,
    cancelText: i18n.getString(_TL_('Go back')),
    onCancel: _.noop,
    className: 'smart-page-booking-cancel-dialog',
  });

  // Handler for clicking "Edit Appointment"
  const onEditAppointment = useCallback((item) => {
    setEditingItem(item);
    setIsEditingModalVisible(true);
    onInternalModalChange({ isOpen: true });
  }, [onInternalModalChange]);

  // Handler for click "Cancel the appointment"
  const onCancelAppointment = useCallback((item) => {
    setEditingItem(item);
    showCancelAppointmentDialog();
  }, [showCancelAppointmentDialog]);

  const onCloseModalInternal = useCallback((e, shouldCloseAllModal) => {
    setIsEditingModalVisible(false);
    onInternalModalChange({ isOpen: false, shouldCloseAllModal });
  }, [onInternalModalChange]);

  // Handler for clicking "submit" button
  const onSubmit = useCallback(() => {
    if (_.isFunction(submitFnRef.current)) {
      submitFnRef.current();
    }
  }, []);

  const onRenderHeader = useCallback(() => (
    <div className={classes.editingHeader}>
      {
        showBack && (
          <div className="back-to-list">
            <IconButton
              iconProps={backIcon}
              onClick={onCloseModalInternal}
              ariaLabel={i18n.getString(_TL_('Back to appointments'))}
            />
            <span>{i18n.getString(_TL_('Back to appointments'))}</span>
          </div>
        )
      }
      <div className="title">{i18n.getString(_TL_('Edit appointment'))}</div>
    </div>
  ), [classes, onCloseModalInternal, i18n, showBack]);

  // DetailList columns config
  const columns = useMemo(() => getColumns({
    i18n,
    classes,
    onEditAppointment,
    onCancelAppointment,
    currentTime: new Date(),
    timeZoneOffset: customerTimeZoneOffset,
  }), [classes, i18n, onEditAppointment, onCancelAppointment, customerTimeZoneOffset]);

  const bookingList = useMemo(() => {
    if (isFetchBookings || isUpdateBooking) {
      return <Spinner label={i18n.getString(_TL_('Loading'))} />;
    }

    if (_.isEmpty(existingBookings)) {
      return <div className="empty-message">{i18n.getString(_TL_('No upcoming bookings'))}</div>;
    }

    return (
      <div className="existing-bookings-detail-list">
        <DetailsList
          columns={columns}
          items={existingBookings}
          selectionMode={SelectionMode.none}
        />
      </div>
    );
  }, [columns, existingBookings, i18n, isFetchBookings, isUpdateBooking]);

  useEffect(() => {
    if (shouldFetchBookings && smartPageId) {
      setIsFetchBookings(true);
      logger.logTrace('CTA|BookAppointment|FetchExistingBookings');

      smartPageService.getExistingBookings(smartPageId).then((val) => {
        setExistingBookings(val);
      }).catch((error) => {
        logger.logError('CTAError|BookAppointment|FetchExistingBookings', error);
        throw new Error(error);
      }).finally(() => {
        setShouldFetchBookings(false);
        setIsFetchBookings(false);
      });
    }
  }, [shouldFetchBookings, smartPageId, smartPageService, logger]);

  const containerClassName = classNames(
    className,
    classes.container,
    'appointment-management-container'
  );

  return (
    <div className={containerClassName}>
      <div className={classNames(classes.listContainer, 'list-container')}>
        <h2 className="list-header">{i18n.getString(title)}</h2>
        <div className="list-content">{bookingList}</div>
      </div>
      {
        isEditingModalVisible && (
          <SmartBookingModal
            isOpen
            className="editing-booking-modal"
            i18n={i18n}
            isLoading={isUpdateBooking}
            actionButtonLabel={i18n.getString(_TL_('Update appointment'))}
            onRenderHeader={onRenderHeader}
            onCloseModal={e => onCloseModalInternal(e, true)}
            onClickFooterButton={onSubmit}
            modalStyle={modalStyle}
          >
            <AppointmentEdit
              i18n={i18n}
              logger={logger}
              smartPageId={smartPageId}
              submitFnRef={submitFnRef}
              bookingDetail={editingItem}
              existingBookings={existingBookings}
              appointmentHours={appointmentHours}
              customerTimeZoneOffset={customerTimeZoneOffset}
              updateAppointment={upsertSmartPageBookings}
              onFinished={onCloseModalInternal}
            />
          </SmartBookingModal>
        )
      }
      { isCancelAppointmentDialogVisible && cancelAppointmentDialog }
    </div>
  );
};

UnStyledSmartAppointmentManagement.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    listContainer: PropTypes.string.isRequired,
    container: PropTypes.string.isRequired,
    editingHeader: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
  onInternalModalChange: PropTypes.func,
  logger: PropTypes.shape({
    logError: PropTypes.func,
    logTrace: PropTypes.func,
  }),
  title: PropTypes.string.isRequired,
  appointmentHours: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string,
    isOpenAllDay: PropTypes.bool,
    timeSlots: PropTypes.arrayOf(PropTypes.shape({})),
    isValid: PropTypes.bool,
  })).isRequired,
  userLocale: PropTypes.string.isRequired,
  smartPageId: PropTypes.string.isRequired,
  smartPageService: PropTypes.shape({}).isRequired,
  customerEmailAddress: PropTypes.string.isRequired,
  customerTimeZoneOffset: PropTypes.number.isRequired,
  businessName: PropTypes.string.isRequired,
  modalStyle: PropTypes.shape({}),
  showBack: PropTypes.bool,
};

UnStyledSmartAppointmentManagement.defaultProps = {
  logger: {
    logError: _.noop,
    logTrace: _.noop,
  },
  className: '',
  onInternalModalChange: _.noop,
  modalStyle: editingModalStyle,
  showBack: true,
};

export const SmartAppointmentManagement = React.memo(withDefaultStyles(
  UnStyledSmartAppointmentManagement,
  getStyles
));
